import { type ComponentType, createElement, type ReactNode } from "react";
import type {
    AccessorFn,
    HeaderContext,
    RowData,
    SortFn,
} from "@tanstack/react-table";
import type { CellTextTone } from "../cells/CellText";
import { RowCellTooltipTarget } from "../cells/CellTooltipTarget";
import { VariantCell } from "../cells/cellVariant";
import { DraftInputSlot } from "../cells/DraftRowInput";
import { TreeCell } from "../cells/ExpandCell";
import { LabelSlots } from "../cells/LabelSlots";
import { SummaryCell } from "../cells/summaryRow";
import type { DataGridCellProps } from "../cells/types";
import { ListEditCell } from "../editing/list/ListEditCell";
import type { DataGridFeatures } from "../features";
import { toAggregationFn } from "../features/aggregation";
import type { DataGridSkeleton } from "../states/skeletonTypes";
import { applyMetaDefaults, COLUMN_KIND_DEFAULTS } from "./columnKinds";
import type { DataGridLabelSlotOptions } from "./kindOptions";
import type { DataGridListEditor } from "./listEditors";
import type {
    DataGridCellContext,
    DataGridColumn,
    DataGridColumnKind,
    DataGridColumnMeta,
    DataGridColumnOptions,
    DataGridDisplayColumnOptions,
    DataGridResolvedColumnMeta,
    DataGridValueSource,
} from "./types";

/** A builder's value type: every kind reads `null` and `undefined` as empty. */
export type Nullable<TValue> = TValue | null | undefined;

/**
 * Renders one body cell from its context and the column's resolved meta.
 * Renderers run during the cell component's render, so they may read context.
 */
export type DataGridCellRenderer<TData extends RowData> = (
    context: DataGridCellContext<TData>,
    meta: DataGridResolvedColumnMeta,
) => ReactNode;

interface DataColumnConfig<TData extends RowData> {
    kind: DataGridColumnKind;
    id: string;
    accessorFn: AccessorFn<TData>;
    /** Only `col.text` exposes the slot options. */
    options: DataGridColumnOptions<TData> & DataGridLabelSlotOptions<TData>;
    showYear?: boolean;
    showPositive?: boolean;
    tone?: CellTextTone;
    sortFn: SortFn<DataGridFeatures, TData>;
    /** The kind's cell; `options.render` replaces it. */
    cell: DataGridCellRenderer<TData>;
    /** The kind's placeholder for these options; `options.skeleton` replaces it. */
    skeleton?: DataGridSkeleton;
}

interface DisplayColumnConfig<TData extends RowData> {
    kind: DataGridColumnKind;
    id: string;
    options: DataGridDisplayColumnOptions<TData>;
    cell: DataGridCellRenderer<TData>;
    skeleton?: DataGridSkeleton;
    header?: (context: HeaderContext<DataGridFeatures, TData>) => ReactNode;
    /** Content width in px; the column adds the cell padding unless `options.size` is set. */
    contentWidth?: number;
}

/** Adapts a `DataGridCellProps` component to a cell renderer, with the cell's `variant`. */
export const cellOf =
    <TData extends RowData>(
        renderer: ComponentType<DataGridCellProps<TData>>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(VariantCell<TData>, { renderer, context, meta });

export const toAccessor = <TData extends RowData, TValue>(
    source: DataGridValueSource<TData, TValue>,
) => {
    if (typeof source === "string") {
        return {
            id: source,
            accessorFn: (row: TData) => row[source] ?? undefined,
        };
    }
    return {
        id: source.id,
        accessorFn: (row: TData) => source.value(row) ?? undefined,
    };
};

const toCell =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
        meta: DataGridResolvedColumnMeta,
    ) =>
    (context: DataGridCellContext<TData>) =>
        cell(context, meta);

const withTreeToggle =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
        { leading, leadingPlacement }: DataGridLabelSlotOptions<TData>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(TreeCell<TData>, {
            context,
            meta,
            value: cell(context, meta),
            slot: leadingPlacement === "toggleSlot" ? leading : undefined,
        });

/** List editing: the column's display inside its editor's trigger. */
const withListEditor =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
        editor: DataGridListEditor<TData>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(ListEditCell<TData>, {
            context,
            meta,
            editor,
            display: cell(context, meta),
        });

/**
 * Draft rows: the create input in place of the draft column's cell and its
 * editor. The context read lives in its own component, so wrappers may call
 * this renderer conditionally. Rows without drafts read the default.
 */
const withDraftInput =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(DraftInputSlot, {
            columnId: context.column.id,
            children: cell(context, meta),
        });

/** `leading` and `trailing` beside the cell (around its editor's trigger), and the copy ID. */
const withLabelSlots =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
        slots: DataGridLabelSlotOptions<TData>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(LabelSlots<TData>, {
            context,
            meta,
            slots,
            label: cell(context, meta),
        });

/** `parentRows: "summary"`: the column's `summary` on parent rows, its cell elsewhere. */
const withSummary =
    <TData extends RowData>(
        cell: DataGridCellRenderer<TData>,
        summary: DataGridCellRenderer<TData>,
    ): DataGridCellRenderer<TData> =>
    (context, meta) =>
        createElement(SummaryCell, {
            summary: summary(context, meta),
            display: cell(context, meta),
        });

/** The column's `tooltip` for the row, anchored to what `cell` draws. */
const withTooltip = <TData extends RowData>(
    cell: DataGridCellRenderer<TData>,
    tooltip: DataGridColumnOptions<TData>["tooltip"],
): DataGridCellRenderer<TData> =>
    tooltip === undefined
        ? cell
        : (context, meta) =>
              createElement(RowCellTooltipTarget<TData>, {
                  context,
                  tooltip,
                  children: cell(context, meta),
              });

/** The kind's (or `render`'s) cell with its tooltip, inside its editor, label slots, and summary. */
const composeCell = <TData extends RowData>(
    kindCell: DataGridCellRenderer<TData>,
    options: DataColumnConfig<TData>["options"],
) => {
    const cell = withTooltip(kindCell, options.tooltip);
    const editable = withDraftInput(
        options.edit ? withListEditor(cell, options.edit) : cell,
    );
    const hasLabelSlots =
        options.leading !== undefined ||
        options.trailing !== undefined ||
        options.copyId !== undefined;
    const labelled = hasLabelSlots
        ? withLabelSlots(editable, {
              leading: options.leading,
              leadingPlacement: options.leadingPlacement,
              trailing: options.trailing,
              copyId: options.copyId,
              copyLabel: options.copyLabel,
          })
        : editable;
    return options.summary
        ? withSummary(
              labelled,
              withTooltip(cellOf(options.summary), options.tooltip),
          )
        : labelled;
};

/** A sortable, resizable column that reads a value from each row. */
export const dataColumn = <TData extends RowData>(
    config: DataColumnConfig<TData>,
): DataGridColumn<TData> => {
    const { options } = config;
    const defaults = COLUMN_KIND_DEFAULTS[config.kind];
    const meta: DataGridColumnMeta = {
        kind: config.kind,
        align: options.align ?? defaults.align,
        empty: options.empty ?? "dash",
        overflow: options.overflow ?? "truncate",
        layout: options.edit?.layout ?? options.layout ?? "fit",
        fill: options.fill,
        fillUntilResized: options.fillUntilResized ?? false,
        defaultMaxSize: options.maxSize,
        pin: options.pin,
        showYear: config.showYear ?? false,
        showPositive: config.showPositive ?? false,
        tone: config.tone ?? "primary",
        aggregate: options.aggregate,
        editor: options.editor,
        isListEditable: options.edit !== undefined,
        editorPopup: options.edit?.popup,
        skeleton: options.skeleton ?? config.skeleton ?? defaults.skeleton,
        tree: options.tree ?? false,
        expandAll: options.expandAll ?? false,
        hasSummary: options.summary !== undefined,
        headerLabel: options.header,
        ariaLabel: options.ariaLabel,
        menuItems: options.menuItems,
        variants: options.variants,
    };
    const cell = composeCell(
        options.render ? cellOf(options.render) : config.cell,
        options,
    );
    return {
        id: config.id,
        accessorFn: config.accessorFn,
        header: options.header,
        size: options.size ?? defaults.size,
        minSize: options.minSize ?? defaults.minSize,
        enableSorting: options.enableSorting ?? defaults.isSortable,
        enableHiding: options.enableHiding,
        enableResizing: options.enableResizing ?? defaults.isResizable,
        enableCellSelection: defaults.isNavigable,
        sortFn: config.sortFn,
        sortUndefined: "last",
        aggregationFn: toAggregationFn<TData>(options.aggregate),
        meta,
        cell: toCell(
            options.tree ? withTreeToggle(cell, options) : cell,
            applyMetaDefaults(meta, options.header),
        ),
    };
};

/** A fixed-width column with no value (select, expand, actions, row number). */
export const displayColumn = <TData extends RowData>(
    config: DisplayColumnConfig<TData>,
): DataGridColumn<TData> => {
    const { options } = config;
    const defaults = COLUMN_KIND_DEFAULTS[config.kind];
    const label = options.header ?? config.id;
    const meta: DataGridColumnMeta = {
        kind: config.kind,
        align: defaults.align,
        empty: "blank",
        layout: options.layout ?? "fit",
        pin: options.pin,
        skeleton: options.skeleton ?? config.skeleton ?? defaults.skeleton,
        headerLabel: label,
        ariaLabel: label,
        ...(options.size === undefined &&
            config.contentWidth !== undefined && {
                contentWidth: config.contentWidth,
            }),
    };
    const size = options.size ?? defaults.size;
    return {
        id: options.id ?? config.id,
        header: config.header,
        size,
        minSize: size,
        maxSize: size,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enableCellSelection: defaults.isNavigable,
        meta,
        cell: toCell(
            options.render ? cellOf(options.render) : config.cell,
            applyMetaDefaults(meta, label),
        ),
    };
};
